import { Box } from '@mui/material'
import {
  CategoryScale,
  Chart as ChartJS,
  Decimation,
  Legend as LegendPlugin,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import zoomPlugin from 'chartjs-plugin-zoom'
import { memo } from 'react'
import { Line } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  LegendPlugin,
  zoomPlugin,
  Decimation
)

type Point = { x: number; y: number }

export type LineSeries = {
  label: string
  data: Point[]
  color: string
}

type Props = {
  series: LineSeries[]
  height?: number
}

const ZOOM_SPEED = 0.1
const LEGEND_MARK_PX = 10
const CHART_MAX_HEIGHT_PX = 220

const datasetBorderColor = (borderColor: unknown): string => {
  if (typeof borderColor === 'string') return borderColor
  if (Array.isArray(borderColor) && typeof borderColor[0] === 'string') {
    return borderColor[0]
  }
  return '#000000'
}

const getOptions = (showLegend: boolean) => ({
  maintainAspectRatio: false,
  parsing: false,
  plugins: {
    legend: {
      display: showLegend,
      position: 'top' as const,
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        pointStyleWidth: LEGEND_MARK_PX,
        boxWidth: LEGEND_MARK_PX,
        boxHeight: LEGEND_MARK_PX / Math.SQRT2,
        generateLabels: function (chart: any) {
          const items = (LegendPlugin.defaults as any).labels.generateLabels.call(
            this,
            chart
          )
          return items.map((item: any) => {
            const ds = chart.data.datasets[item.datasetIndex]
            const color = datasetBorderColor(ds.borderColor)
            return { ...item, fillStyle: color, strokeStyle: color, lineWidth: 0 }
          })
        },
      },
    },
    decimation: {
      enabled: true,
      algorithm: 'lttb',
      samples: 50,
      threshold: 50,
    },
    zoom: {
      pan: { enabled: true, mode: 'x' as const },
      zoom: {
        wheel: { enabled: true, speed: ZOOM_SPEED },
        pinch: { enabled: true },
        drag: { enabled: false },
        mode: 'x' as const,
      },
    },
  },
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'hour' as const,
        displayFormats: {
          minute: 'HH:mm',
          hour: 'HH:mm',
          day: 'dd.MM',
          month: 'MM',
        },
        tooltipFormat: 'dd.MM.yyyy HH:mm',
      },
    },
    y: {
      beginAtZero: false,
    },
  },
})

const SimpleLineChart = ({ series, height = CHART_MAX_HEIGHT_PX }: Props) => {
  const chartHeight = Math.min(height, CHART_MAX_HEIGHT_PX)
  const showLegend = series.length > 1
  const chartData = {
    datasets: series.map((s) => ({
      label: s.label,
      data: s.data,
      borderColor: s.color,
      backgroundColor: s.color,
      fill: false,
      tension: 0.3,
      pointRadius: 3,
      pointBackgroundColor: '#fff',
      pointBorderColor: s.color,
    })),
  }

  return (
    <Box
      width='100%'
      height={chartHeight}
    >
      <Line
        data={chartData}
        options={getOptions(showLegend) as any}
      />
    </Box>
  )
}

export default memo(SimpleLineChart)
